import type { FileItem } from "../FileGrid";

export type MoveSource = {
  key: string;
  isDirectory: boolean;
};

export type MoveConflict = {
  source: MoveSource;
  target: string;
};

export type MoveConflictPolicy = "skip" | "overwrite";

export type MoveProgress = {
  done: number;
  total: number;
  phase: "counting" | "overwriting" | "copying" | "deleting" | "done";
  current?: string;
  completedItems: number;
  totalItems: number;
};

export type MoveFailure = {
  source: string;
  error: Error;
};

export type MoveResult = {
  succeeded: string[];
  skipped: string[];
  failed: MoveFailure[];
};

export function normalizeDirectory(path: string) {
  const normalized = path.replace(/\/+$/, "");
  return normalized ? `${normalized}/` : "";
}

export function pathBasename(path: string) {
  return path.replace(/\/+$/, "").split("/").pop() ?? "";
}

export function parentDirectory(path: string) {
  const parts = path.replace(/\/+$/, "").split("/");
  parts.pop();
  return parts.length ? `${parts.join("/")}/` : "";
}

export function targetForSource(sourceKey: string, destination: string) {
  return `${normalizeDirectory(destination)}${pathBasename(sourceKey)}`;
}

export function validateMoveDestination(
  sources: MoveSource[],
  destination: string
) {
  const normalizedDestination = normalizeDirectory(destination);
  const destinationKey = normalizedDestination.replace(/\/$/, "");

  return sources
    .filter((source) => {
      const sourceKey = source.key.replace(/\/+$/, "");
      const target = targetForSource(sourceKey, normalizedDestination);
      return (
        target === sourceKey ||
        (source.isDirectory &&
          (destinationKey === sourceKey ||
            normalizedDestination.startsWith(`${sourceKey}/`)))
      );
    })
    .map((source) => source.key);
}

export function findMoveConflicts(
  sources: MoveSource[],
  destination: string,
  destinationItems: FileItem[]
) {
  const existing = new Set(
    destinationItems.map((item) => item.key.replace(/\/+$/, ""))
  );

  return sources
    .map((source) => ({
      source,
      target: targetForSource(source.key, destination),
    }))
    .filter(({ target }) => existing.has(target));
}
